/**
 * Library — "Jobs To Be Done" knowledge hub aggregating Central
 * Rentals' AEO answers, guides, sustainability content, mortgage rates,
 * and competitor comparisons. Each card has prominent share buttons
 * (LinkedIn / Facebook / X / Copy link) so visitors can spread the
 * content into their networks — the free-tier sharing + branding loop.
 *
 * Content lives in three places already; we don't re-build it, we just
 * aggregate + decorate:
 *   1. /api/aeo/answers          (78 EN + 6 FR JTBD answers)
 *   2. /vs/{competitor}          (7 competitor comparisons)
 *   3. /mortgage-rates, /sustainability, /credit-builder (hubs)
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import BackToPortalButton from "@/components/BackToPortalButton";
import { DirectAnswer, ArticleSchema, BrandStory } from "@/components/seo/DirectAnswer";
import { Sparkles, Share2, Search, ExternalLink, Linkedin, Facebook, Twitter, Copy, Check } from "lucide-react";
import PublicHeader from "@/components/layout/PublicHeader";
import SmartChatBubble from "@/components/SmartChatBubble";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const SITE = "https://centralrentals.ca";

const HUBS = [
  { url: `${SITE}/mortgage-rates`,   title: "Canadian mortgage rates — 15 lenders, live",            tag: "Money",    desc: "Compare Big 6 + monoline + credit unions across 4 products. Smart picks for investment properties." },
  { url: `${SITE}/sustainability`,   title: "Sustainability scoring for Canadian rental portfolios", tag: "Climate",  desc: "Free Health Check + 13 province retrofit lever guides. ON EWRB + BC Step Code + QC Bill 41 ready." },
  { url: `${SITE}/credit-builder`,   title: "Tenant credit builder — report on-time rent to bureaus", tag: "Tenants",  desc: "Help your tenants build credit history while you pay zero extra. 13-province coverage." },
  { url: `${SITE}/pricing`,          title: "Pricing — CA$35/mo (DIY), scales as you grow",       tag: "Pricing",  desc: "Drag the Growth Calculator from 1 to 100 units to see your real cost." },
  { url: `${SITE}/vs/buildium`,      title: "Central Rentals vs Buildium",                             tag: "Compare",  desc: "Why Canadian landlords leave Buildium for us." },
  { url: `${SITE}/vs/yardi`,         title: "Central Rentals vs Yardi",                                tag: "Compare",  desc: "Same screening + accounting, no $50K onboarding." },
  { url: `${SITE}/vs/appfolio`,      title: "Central Rentals vs AppFolio",                             tag: "Compare",  desc: "Half the per-door cost, all the Canadian taxes." },
];

function setMeta(sel, attrs) {
  let el = document.head.querySelector(sel);
  if (!el) { el = document.createElement(sel.startsWith("link") ? "link" : "meta"); document.head.appendChild(el); }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  return el;
}

function ShareRow({ url, title, testid }) {
  const [done, setDone] = useState(false);
  const enc = encodeURIComponent;
  const li = `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`;
  const fb = `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`;
  const tw = `https://twitter.com/intent/tweet?url=${enc(url)}&text=${enc(title)}`;
  return (
    <div className="flex items-center gap-1.5 mt-3" data-testid={`${testid}-share`}>
      <a href={li} target="_blank" rel="noopener noreferrer" title="Share on LinkedIn" data-testid={`${testid}-share-linkedin`} className="p-1.5 rounded-md text-slate-500 hover:text-blue-700 hover:bg-blue-50"><Linkedin className="w-4 h-4"/></a>
      <a href={fb} target="_blank" rel="noopener noreferrer" title="Share on Facebook"  data-testid={`${testid}-share-facebook`} className="p-1.5 rounded-md text-slate-500 hover:text-blue-700 hover:bg-blue-50"><Facebook className="w-4 h-4"/></a>
      <a href={tw} target="_blank" rel="noopener noreferrer" title="Share on X"          data-testid={`${testid}-share-twitter`} className="p-1.5 rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100"><Twitter className="w-4 h-4"/></a>
      <button onClick={() => { navigator.clipboard.writeText(url); setDone(true); setTimeout(() => setDone(false), 1500); }} title="Copy link" data-testid={`${testid}-share-copy`} className="p-1.5 rounded-md text-slate-500 hover:text-emerald-700 hover:bg-emerald-50">
        {done ? <Check className="w-4 h-4 text-emerald-600"/> : <Copy className="w-4 h-4"/>}
      </button>
    </div>
  );
}

export default function Library() {
  const [answers, setAnswers] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Library — Canadian landlord guides + how-tos · Central Rentals";
    const desc = "Free, well-cited tutorials: how to file T776, screen tenants, raise rent, write notices — built for Canadian landlords. Share to your network.";
    const tags = [
      setMeta('meta[name="description"]', { name: "description", content: desc }),
      setMeta('link[rel="canonical"]', { rel: "canonical", href: `${SITE}/library` }),
      setMeta('meta[property="og:title"]', { property: "og:title", content: "Central Rentals — Canadian landlord Library" }),
      setMeta('meta[property="og:description"]', { property: "og:description", content: desc }),
    ];
    // Collection schema — helps generative AI cite this page as an index.
    let s = document.head.querySelector('script[data-jsonld="library"]');
    if (!s) { s = document.createElement("script"); s.type = "application/ld+json"; s.setAttribute("data-jsonld", "library"); document.head.appendChild(s); }
    s.textContent = JSON.stringify({
      "@context": "https://schema.org", "@type": "CollectionPage",
      "name": "Central Rentals Library — Canadian Landlord Knowledge Hub",
      "description": desc,
      "url": `${SITE}/library`,
    });
    return () => { tags.forEach((t) => t?.remove?.()); s?.remove?.(); };
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/aeo/answers`);
        const d = await r.json();
        setAnswers(d.items || d.answers || []);
      } catch { /* noop */ } finally { setLoading(false); }
    })();
  }, []);

  const filtered = useMemo(() => {
    const raw = q.trim().toLowerCase();
    if (!raw) return answers;
    // Fuzzy-normaliser: strip diacritics, punctuation, and simple English
    // plural endings ("rents" → "rent", "leases" → "lease", "ies" → "y").
    // Cheap, dependency-free — good enough for a search box over 200-ish
    // help articles. Also splits multi-word queries so any token matches.
    const normalize = (s) => {
      let n = (s || "").toString().toLowerCase()
        .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9 ]+/g, " ")
        .trim();
      n = n.replace(/\bies\b/g, "y")
           .replace(/([a-z]{3,})s\b/g, "$1"); // drop trailing s on 3+ letter words
      return n;
    };
    const needleTokens = normalize(raw).split(/\s+/).filter(Boolean);
    if (needleTokens.length === 0) return answers;
    return answers.filter((a) => {
      // The AEO API returns { question, topic, province_name, province_slug,
      // province_code, intent, url } — plus optional legacy fields. Cover
      // ALL of them so a term like "deposit" surfaces the security-deposit
      // articles regardless of which field label the entry uses.
      const hay = normalize([
        a.title, a.question, a.topic, a.intent, a.tag, a.desc,
        a.province, a.province_name, a.province_slug, a.province_code,
        a.slug, a.url,
      ].filter(Boolean).join(" "));
      // Every token must appear somewhere in the haystack.
      return needleTokens.every((t) => hay.includes(t));
    });
  }, [q, answers]);

  return (
    <div className="min-h-screen bg-white" data-testid="library-page">
      <PublicHeader light/>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-5 pt-12 pb-6">
        <div className="inline-flex items-center gap-2 mb-5 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-[11px] font-bold tracking-wide text-amber-800">
          <Sparkles className="w-3.5 h-3.5"/> Designed to be cited by ChatGPT, Perplexity & Google search overviews
        </div>
        <h1 className="font-display text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tighter text-slate-900">
          Canadian landlord Library
        </h1>
        <p className="mt-5 text-lg text-slate-600 max-w-2xl">
          Free tutorials and templates for every job a Canadian landlord
          actually does — written in the &quot;Job To Be Done&quot; format that
          generative search engines love to quote. Use them yourself, or share them
          with your network in one click.
        </p>

        <div className="mt-6">
          <DirectAnswer
            question="What is the Central Rentals Library?"
            answer="The Central Rentals Library is a free knowledge base of Canadian landlord and tenant articles covering every province — Ontario N-forms, BC Section 13, Alberta RTDRS, Québec TAL, tax exports (T776/NR4), tenant screening, rent collection, and more. Every answer is short, source-cited to the official provincial register, and updated weekly. Search below or browse the hubs."
          />
          <ArticleSchema
            headline="Canadian landlord Library — Central Rentals Canada"
            description="Free tutorials and answer templates for Canadian landlords across all 13 provinces. Written in a job-to-be-done format optimized for generative search engines."
            datePublished="2026-01-15"
            articleSection="Knowledge base"
            keywords={["Canada landlord", "property management", "T776", "N4", "N12", "BC RTB", "Québec TAL", "Alberta RTDRS", "landlord software"]}
          />
        </div>

        <div className="mt-7 max-w-xl">
          <label htmlFor="library-search" className="sr-only">Search the library</label>
          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"/>
            <input
              id="library-search"
              data-testid="library-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search: T776, eviction, rent increase, BC, Quebec…"
              className="w-full pl-10 pr-3 py-3 text-sm border border-slate-300 rounded-xl outline-none focus:border-emerald-500 shadow-sm"
            />
          </div>
        </div>
      </section>

      {/* Hubs strip */}
      <section className="max-w-6xl mx-auto px-5 py-6" data-testid="library-hubs">
        <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500 mb-3">Knowledge hubs</div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {(() => {
            // Apply the same search filter (with plural→singular
            // normalisation) to the hubs cards so typing narrows both
            // sections at once. Previously the hubs stayed static
            // regardless of the query — and "Rents" wouldn't match a
            // "Rent" hub title.
            const normalizeHub = (s) =>
              (s || "").toString().toLowerCase()
                .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
                .replace(/[^a-z0-9 ]+/g, " ")
                .trim()
                .replace(/\bies\b/g, "y")
                .replace(/([a-z]{3,})s\b/g, "$1");
            const tokens = normalizeHub(q).split(/\s+/).filter(Boolean);
            const shown = tokens.length === 0
              ? HUBS
              : HUBS.filter((h) => {
                  const hay = normalizeHub(`${h.title} ${h.tag} ${h.desc} ${h.url}`);
                  return tokens.every((t) => hay.includes(t));
                });
            if (shown.length === 0) {
              return (
                <div className="col-span-full rounded-xl border border-dashed border-slate-300 bg-slate-50/50 p-6 text-center text-sm text-slate-500" data-testid="library-hubs-empty">
                  No knowledge hubs match &ldquo;{q}&rdquo;. Try broader terms like <em>rent</em>, <em>tenant</em>, or <em>tax</em>.
                </div>
              );
            }
            return shown.map((h) => (
            <div key={h.url} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm flex flex-col" data-testid={`library-hub-${h.url.split('/').pop()}`}>
              <span className="self-start text-[10px] uppercase tracking-widest font-bold text-violet-700 bg-violet-50 px-2 py-0.5 rounded-full mb-2">{h.tag}</span>
              <a href={h.url} className="font-display font-extrabold text-slate-900 hover:text-emerald-700 leading-snug">{h.title}</a>
              <p className="mt-1 text-xs text-slate-500 flex-1">{h.desc}</p>
              <div className="mt-3 flex items-center justify-between">
                <a href={h.url} className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700"><ExternalLink className="w-3 h-3"/> Open</a>
                <ShareRow url={h.url} title={h.title} testid={`library-hub-${h.url.split('/').pop()}`}/>
              </div>
            </div>
            ));
          })()}
        </div>
      </section>

      {/* JTBD Answers (from existing AEO service) */}
      <section className="max-w-6xl mx-auto px-5 py-10 border-t border-slate-100" data-testid="library-jtbd">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500">Jobs To Be Done</div>
            <div className="font-display text-2xl font-extrabold text-slate-900">Step-by-step answers, AEO-formatted</div>
          </div>
          <div className="text-xs text-slate-500"><Share2 className="w-4 h-4 inline -mt-0.5"/> Every card shares to LinkedIn, Facebook, X</div>
        </div>
        {loading ? (
          <div className="text-center py-12 text-slate-500 text-sm">Loading library…</div>
        ) : filtered.length === 0 ? (
          <div className="rounded-xl border-2 border-dashed border-slate-200 p-10 text-center text-slate-500">
            No answers match &quot;{q}&quot; — try a province name (Ontario, BC, Quebec) or topic (T776, eviction).
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="library-jtbd-grid">
            {filtered.slice(0, 30).map((a, idx) => {
              const provinceSlug = a.province_slug || a.province || "";
              const slug = a.slug || a.topic || "";
              const url = `${SITE}${a.url || `/answers/${provinceSlug}/${slug}`}`;
              const title = a.title || a.question || slug || "Answer";
              const k = a.id || `${provinceSlug || "ca"}-${slug || idx}`;
              const provinceLabel = (a.province_code || a.province || provinceSlug || "").toUpperCase();
              return (
                <article key={k} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm flex flex-col" data-testid={`library-card-${slug || idx}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] uppercase tracking-widest font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">{provinceLabel}</span>
                    {a.lang === "fr" && <span className="text-[10px] uppercase tracking-widest font-bold text-rose-700 bg-rose-50 px-2 py-0.5 rounded-full">FR</span>}
                  </div>
                  <Link to={a.url || `/answers/${provinceSlug}/${slug}`} className="font-bold text-slate-900 hover:text-emerald-700 leading-snug text-sm">{title}</Link>
                  <div className="mt-auto flex items-center justify-between pt-3">
                    <Link to={a.url || `/answers/${provinceSlug}/${slug}`} className="text-[11px] font-bold text-emerald-700 hover:text-emerald-800">Read →</Link>
                    <ShareRow url={url} title={title} testid={`library-card-${slug || idx}`}/>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      <div className="max-w-6xl mx-auto px-5 pb-8">
        <BrandStory />
      </div>

      <BackToPortalButton />
      <SmartChatBubble/>
    </div>
  );
}
